// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { ActionMenuItem } from '@/components/action-menu-item';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import {
  AddRounded,
  ArrowDropDownRounded,
  MultipleStopOutlined,
  SyncOutlined,
} from '@/constants/hardfin-standard-icons';
import { SortDirection, invert, sortDirectionSchema } from '@/constants/sort';
import {
  CostFilter,
  CostSortProperty,
  costSortPropertySchema,
} from '@/costs/api/get-costs';
import { CostAnalytics } from '@/costs/components/cost-analytics';
import { CostDataImportDialog } from '@/costs/components/cost-data-import-dialog';
import { CostDataUpdateDialog } from '@/costs/components/cost-data-update-dialog';
import { CostDialog } from '@/costs/components/cost-dialog';
import { CostFilterBar } from '@/costs/components/cost-filter-bar';
import { CostTableSkeleton } from '@/costs/components/cost-table-skeleton';
import { CostTableView } from '@/costs/components/cost-table-view';
import { CostViewModeToggle } from '@/costs/components/cost-view-mode-toggle';
import { CostsSummary } from '@/costs/components/costs-summary';
import { useDisclosure } from '@/hooks/use-disclosure';
import { TrackingViewMode, trackingViewModeSchema } from '@/report/types';
import { searchTermSchema } from '@/utilities/search-term';
import {
  Button,
  ButtonGroup,
  Container,
  Menu,
  Stack,
  debounce,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import { DateTime } from 'luxon';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';

const DEFAULT_SORT_PROPERTY: CostSortProperty = 'costIncurredOn';
const DEFAULT_SORT_DIRECTION: SortDirection = 'desc';

const costSearchSchema = z.object({
  sort: costSortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
  page: z.number().optional().catch(undefined),
  search: searchTermSchema,
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  people: z.array(z.string()).optional(),
  projects: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
  add: z.boolean().optional().catch(true),
  view: trackingViewModeSchema.optional().catch('table'),
});

export const Route = createFileRoute('/_app/costs/')({
  component: CostsRoute,
  validateSearch: costSearchSchema,
});

function CostsRoute() {
  const navigate = Route.useNavigate();
  const searchParameters = Route.useSearch();
  const queryClient = useQueryClient();
  const createCostDialog = useDisclosure(false);
  const uploadDialog = useDisclosure();
  const updateDialog = useDisclosure();
  const actionMenu = useDisclosure();
  const openCreateCostDialog = createCostDialog.open;

  const actionGroupRef = useRef<HTMLDivElement>(null);
  const didOpenCreateCostDialogFromSearch = useRef(false);
  const { user: currentUser } = useCurrentUser();

  useEffect(() => {
    if (!searchParameters.add) {
      didOpenCreateCostDialogFromSearch.current = false;
      return;
    }

    if (!currentUser || didOpenCreateCostDialogFromSearch.current) {
      return;
    }

    didOpenCreateCostDialogFromSearch.current = true;
    openCreateCostDialog();
  }, [searchParameters.add, currentUser, openCreateCostDialog]);

  const updateSearch = useMemo(
    () =>
      debounce((search: string) => {
        // Reset to page 1 when search changes
        const newSearch = omit(searchParameters, 'page');

        if (search) {
          newSearch.search = search;
        } else {
          delete newSearch.search;
        }

        navigate({ search: newSearch });
      }, 300),
    [navigate, searchParameters]
  );

  // The current sort state is constructed by first checking the URL search
  // parameters. If not present, use the default values.
  const sort = {
    property: searchParameters.sort ?? DEFAULT_SORT_PROPERTY,
    direction: searchParameters.direction ?? DEFAULT_SORT_DIRECTION,
  };

  const viewMode: TrackingViewMode = searchParameters.view ?? 'table';

  const costFilter: CostFilter = {
    pageSize: 50,
    pageNumber: searchParameters.page ?? 1,
    sort,
    search: searchParameters.search,
    startDate: searchParameters.startDate,
    endDate: searchParameters.endDate,
    personIds: searchParameters.people ?? [],
    projectIds: searchParameters.projects ?? [],
    categories: searchParameters.categories ?? [],
  };

  // Memoize the summary filter to prevent unnecessary re-renders and query key changes
  const summaryFilter = useMemo(
    () => ({
      search: searchParameters.search,
      startDate: searchParameters.startDate,
      endDate: searchParameters.endDate,
      personIds: searchParameters.people,
      projectIds: searchParameters.projects,
      categories: searchParameters.categories,
    }),
    [
      searchParameters.search,
      searchParameters.startDate,
      searchParameters.endDate,
      searchParameters.people,
      searchParameters.projects,
      searchParameters.categories,
    ]
  );

  const setSort = (property: CostSortProperty, direction: SortDirection) => {
    const newSearch = { ...searchParameters };

    // Clean up default sort values from URL
    if (property === DEFAULT_SORT_PROPERTY) {
      delete newSearch.sort;
    } else {
      newSearch.sort = property;
    }

    if (direction === DEFAULT_SORT_DIRECTION) {
      delete newSearch.direction;
    } else {
      newSearch.direction = direction;
    }

    // Reset to page 1 when sort changes
    delete newSearch.page;

    navigate({ search: newSearch });
  };

  const handleSort = (property: CostSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }

    setSort(property, 'asc');
  };

  const handleChangePage = (pageNumber: number) => {
    const newSearch = { ...searchParameters };

    // Clean up page 1 from URL (it's the default)
    if (pageNumber === 1) {
      delete newSearch.page;
    } else {
      newSearch.page = pageNumber;
    }

    navigate({ search: newSearch });
  };

  const handleSearchChange = (search: string) => {
    updateSearch(search);
  };

  const handleDateRangeChange = (dateRange: DateRange<DateTime>) => {
    const [startDate, endDate] = dateRange;
    // Reset to page 1 when filters change
    const newSearch = omit(searchParameters, 'page');

    if (startDate) {
      newSearch.startDate = startDate.toISODate({ format: 'extended' });
    } else {
      delete newSearch.startDate;
    }

    if (endDate) {
      newSearch.endDate = endDate.toISODate({ format: 'extended' });
    } else {
      delete newSearch.endDate;
    }

    navigate({ search: newSearch });
  };

  const handlePersonsChange = (people: string[]) => {
    // Reset to page 1 when filters change
    const newSearch = omit(searchParameters, 'page');

    if (people.length === 0) {
      delete newSearch.people;
    } else {
      newSearch.people = people;
    }

    navigate({ search: newSearch });
  };

  const handleProjectsChange = (projects: string[]) => {
    // Reset to page 1 when filters change
    const newSearch = omit(searchParameters, 'page');

    if (projects.length === 0) {
      delete newSearch.projects;
    } else {
      newSearch.projects = projects;
    }

    navigate({ search: newSearch });
  };

  const handleCategoriesChange = (categories: string[]) => {
    // Reset to page 1 when filters change
    const newSearch = omit(searchParameters, 'page');

    if (categories.length === 0) {
      delete newSearch.categories;
    } else {
      newSearch.categories = categories;
    }

    navigate({ search: newSearch });
  };

  const handleCreateCostDialog = () => {
    createCostDialog.open();
    navigate({ search: { ...searchParameters, add: true } });
  };

  const handleClearSearch = () => {
    // Reset to page 1 when clearing search
    navigate({ search: omit(searchParameters, ['search', 'page']) });
  };

  const handleChangeViewMode = (newViewMode: TrackingViewMode) => {
    const newSearch = { ...searchParameters };

    // Clean up default view mode from URL
    if (newViewMode === 'table') {
      delete newSearch.view;
    } else {
      newSearch.view = newViewMode;
    }

    // Reset to page 1 when view mode changes
    delete newSearch.page;

    navigate({ search: newSearch });
  };

  const filterBarProps = {
    search: searchParameters.search,
    startDate: searchParameters.startDate
      ? DateTime.fromISO(searchParameters.startDate)
      : null,
    endDate: searchParameters.endDate
      ? DateTime.fromISO(searchParameters.endDate)
      : null,
    personIds: searchParameters.people ?? [],
    projectIds: searchParameters.projects ?? [],
    categories: searchParameters.categories ?? [],
  };

  return (
    <Container maxWidth="xl">
      <CostDialog
        isOpen={createCostDialog.isOpen}
        onClose={() => {
          createCostDialog.close();
          if (searchParameters.add) {
            navigate({ search: { ...searchParameters, add: undefined } });
          }
        }}
        invalidationKeys={[['costs']]}
      />
      <CostDataImportDialog
        isOpen={uploadDialog.isOpen}
        onClose={uploadDialog.close}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['costs'] });
        }}
      />
      <CostDataUpdateDialog
        isOpen={updateDialog.isOpen}
        onClose={updateDialog.close}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['costs'] });
        }}
      />
      <PageContent>
        <CostsSummary filter={summaryFilter} />
        <Stack paddingTop={1}>
          {/* View mode toggle and action buttons */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <CostViewModeToggle
              value={viewMode}
              onChange={handleChangeViewMode}
            />
            <ButtonGroup size="small" variant="contained" ref={actionGroupRef}>
              <Button
                startIcon={<AddRounded />}
                onClick={handleCreateCostDialog}
              >
                Add cost
              </Button>
              <Button onClick={actionMenu.open}>
                <ArrowDropDownRounded fontSize="small" />
              </Button>
            </ButtonGroup>
            <Menu
              open={actionMenu.isOpen}
              onClose={actionMenu.close}
              onClick={actionMenu.close}
              anchorEl={actionGroupRef.current}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{ disablePadding: true }}
              slotProps={{
                paper: {
                  sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 },
                },
              }}
            >
              <ActionMenuItem
                icon={<MultipleStopOutlined color="action" fontSize="small" />}
                label="Import new from file"
                description="Import new costs in bulk from a CSV file"
                onClick={uploadDialog.open}
              />
              <ActionMenuItem
                icon={<SyncOutlined color="action" fontSize="small" />}
                label="Update existing from file"
                description="Update existing costs in bulk from a CSV file"
                onClick={updateDialog.open}
              />
            </Menu>
          </Stack>

          {/* Filter bar */}
          <CostFilterBar
            filter={filterBarProps}
            onSearchChange={handleSearchChange}
            onDateRangeChange={handleDateRangeChange}
            onPersonsChange={handlePersonsChange}
            onProjectsChange={handleProjectsChange}
            onCategoriesChange={handleCategoriesChange}
            onClearSearch={handleClearSearch}
          />

          {/* Cost table and analytics view */}
          <SupportErrorBoundary message="An error occurred while loading costs.">
            {viewMode === 'table' && (
              <Suspense fallback={<CostTableSkeleton />}>
                <CostTableView
                  sort={sort}
                  filter={costFilter}
                  onSort={handleSort}
                  onPageChange={handleChangePage}
                />
              </Suspense>
            )}

            {viewMode === 'analytics' && (
              <CostAnalytics filter={summaryFilter} />
            )}
          </SupportErrorBoundary>
        </Stack>
      </PageContent>
    </Container>
  );
}
